<template>
    <div :class="$style.container">
        <button
            :data-interface-target="`${authLabel}_like`"
            :aria-label="$tr('gameContainer.like')"
            :class="[$style.like, isLiked && $style.selected]"
            @click="vote(isLiked ? 'reaction_unset_like' : 'reaction_like')"
        >
            <Icon name="like" />
            <Typography
                type="label"
                :responsive="false"
                size="m"
                :class="$style.label"
                bold
            >
                {{ formatNum }}
            </Typography>
        </button>
        <button
            :data-interface-target="`${authLabel}_dislike`"
            :aria-label="$tr('gameContainer.dislike')"
            :class="[$style.dislike, isDisliked && $style.selected]"
            @click="vote(isDisliked ? 'reaction_unset_dislike' : 'reaction_dislike')"
        >
            <Icon name="dislike" />
        </button>
    </div>
</template>
<script setup lang="ts">
import { useMounted } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import Icon from '@/components/Icon/Icon.vue'
import Typography from '@/components/Typography.vue'
import { useLayoutStore } from '@/store/layout-store'
import { useUserStore } from '@/store/user-store/user-store'
import { GameVote, useVotesStore } from '@/store/votes-store'
import { APIGameType } from '@/types'
import { VoteEvent } from '@/types/logger.types'
import { formatValue } from '@/utils/numbers'

const props = defineProps<{ game?: APIGameType }>()

const userStore = useUserStore()
const layoutStore = useLayoutStore()
const votesStore = useVotesStore()

const { isAuthorized } = storeToRefs(userStore)
const { votedGames } = storeToRefs(votesStore)

const isMounted = useMounted()

const authLabel = computed(() => (isAuthorized.value ? 'auth' : 'non_auth'))
const selectedReaction = computed(() =>
    // votedGames is persisted in localStorage, so we need to check if the component is mounted
    isMounted.value && props.game
        ? votedGames.value.find((item: GameVote) => item.hru === props.game?.hru)?.reaction
        : null,
)
const isLiked = computed(() => selectedReaction.value === 'reaction_like')
const isDisliked = computed(() => selectedReaction.value === 'reaction_dislike')
const ratingCount = computed(() => (props.game?.rating?.count || 0) + (isLiked.value ? 1 : 0))
const formatNum = computed(() => (props.game ? formatValue(ratingCount.value, { shorten: true }) : ' '))

function vote(reaction: VoteEvent) {
    if (!props.game) {
        return
    }
    if (!isAuthorized.value) {
        layoutStore.openSignInModal('vote-game')
    } else {
        votesStore.updateGameVote({ hru: props.game.hru, reaction })
    }
}
</script>
<style module>
.container {
    display: flex;
    gap: 0;
}

.like,
.dislike {
    cursor: pointer;
    display: flex;
    gap: 6px;
    height: 48px;
    padding: 16px;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--white-08);
    background: var(--white-08);
}

.like {
    border-radius: 1000px 0 0 1000px;
    border-right-style: none;
}

.dislike {
    border-radius: 0 1000px 1000px 0;
}

.like:hover,
.dislike:hover,
.like:active,
.dislike:active {
    filter: brightness(1.08);
}

.like {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
}

.like.selected {
    color: var(--electric-lime);
}

.dislike {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    padding: 13px 12px;
}

.dislike.selected {
    color: #ff9c9c;
}

.label {
    font-variant-numeric: tabular-nums;
    display: inline-block;
    white-space: nowrap;
}

@media (--tablet-plus) {
    .like,
    .dislike {
        color: #fff;
        background-color: var(--experimental-carbon-purple-400);
        border-color: var(--white-04);
        line-height: 100%;
        height: 40px;
        white-space: nowrap;
        transition: background-color 0.15s;
    }

    .like {
        padding-left: 12px;
        padding-right: 12px;
    }

    .dislike {
        padding-left: 8px;
        padding-right: 12px;
    }
}
</style>
